import {Component, OnInit} from '@angular/core';
import {TimeTrackingService} from '../../shared/time-tracking.service';
import {NotifierService} from '../../shared/notifier.service';
import {TranslateService} from '@ngx-translate/core';
import {ReimbursementRate, TripPurpose} from '../../interfaces/trip';

// A rate as typed, "0,23" or "0.23"; the rate is kept as text until it is sent.
type RateForm = Omit<ReimbursementRate, 'rate'> & {rate: string};

/** The tenant's reimbursement rate per trip purpose and start date (#1515), Admin only. */
@Component({
  selector: 'bst-reimbursement-rates',
  templateUrl: './reimbursement-rates.component.html',
  styles: [`
    .hint { color: var(--text-hint-color); font-size: 0.85rem; }
    .purpose { margin-top: 0.75rem; }
    .rate-row { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; padding: 0.25rem 0; }
    .rate-row input { max-width: 10rem; }
    .warning { flex-basis: 100%; color: var(--color-warning-default); font-size: 0.85rem; }
    @media (max-width: 599px) { button.size-tiny { min-height: 44px; font-size: 0.8rem; } }
  `],
  standalone: false
})
export class ReimbursementRatesComponent implements OnInit {
  readonly purposes: TripPurpose[] = ['business', 'commute'];
  rates: ReimbursementRate[] = [];
  draft: RateForm = this.blank();
  editing: RateForm | null = null;
  busy = false;

  constructor(private time: TimeTrackingService, private notifier: NotifierService, private translate: TranslateService) {}

  // The UI language, passed to the number pipe: LOCALE_ID is fixed at bootstrap.
  get lang(): string {
    return this.translate.currentLang ?? this.translate.defaultLang;
  }

  async ngOnInit() {
    this.rates = await this.time.getReimbursementRates().catch(() => []);
  }

  get isDirty(): boolean {
    return !!this.editing || !!this.draft.rate?.trim() || !!this.draft.starts_on;
  }

  of(purpose: TripPurpose): ReimbursementRate[] {
    return this.rates.filter(r => r.purpose === purpose);
  }

  edit(rate: ReimbursementRate) {
    this.editing = {...rate, rate: String(rate.rate)};
  }

  // Same bounds as the server: 0 to 99.9999, at most 4 decimals, comma or dot.
  valid(form: RateForm): boolean {
    return /^\d{1,2}([.,]\d{1,4})?$/.test(form.rate?.trim() ?? '') && !!form.starts_on;
  }

  async save(form: RateForm) {
    if (this.busy || !this.valid(form)) { return; }
    this.busy = true;
    const saved = await this.time.saveReimbursementRate({...form, rate: Number(form.rate.trim().replace(',', '.'))}).catch(() => null);
    this.busy = false;
    if (!saved) { return; }
    this.editing = null;
    this.draft = this.blank();
    await this.ngOnInit();
  }

  async remove(rate: ReimbursementRate) {
    if (this.busy) { return; }
    // The dialog shows its subtitle as given, so translate it first.
    const check = await this.translate.get('reimbursementrate.deletecheck').toPromise();
    if (!await this.notifier.confirm('sure?', check, 'delete', 'danger')) { return; }
    this.busy = true;
    const removed = await this.time.removeReimbursementRate(rate.id).catch(() => null);
    this.busy = false;
    if (removed) {
      await this.ngOnInit();
    }
  }

  private blank(): RateForm {
    return {purpose: 'business', rate: '', starts_on: ''};
  }
}
